import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';

import ButtonLink from '@dashboard/components/mui-atoms/ButtonLink';
import PlatformSendingStatusAlert from '@dashboard/components/PlatformSendingStatusAlert';
import SamplePlatformOnboardingManager from '@dashboard/components/SamplePlatformOnboardingManager';
import WpSessionDropdown from '@dashboard/components/SessionDropdown';
import { WhiteWaypointLogo } from '@dashboard/components/WaypointLogo';
import { usePlatformsQuery } from '@dashboard/generated/wp/authentication';
import useCurrentWpPlatform from '@dashboard/hooks/useCurrentWpPlatform';
import {
  DynamicFormOutlined,
  PeopleOutlined,
  SettingsOutlined,
  TimelineOutlined,
  WysiwygOutlined,
} from '@mui/icons-material';
import { AppBar, Box, Container, Divider, Stack, Toolbar, Typography } from '@mui/material';

import NavigationLinksDesktop from './NavigationLinksDesktop';
import NavigationLinksMobile from './NavigationLinksMobile';
import PlatformsDropdownMenu from './PlatformsDropdownMenu';

export default function PlatformPageLayout() {
  const { data: pData } = useCurrentWpPlatform();
  const { data } = usePlatformsQuery();
  const platform = pData?.Platform;
  const platforms = data?.Platforms.nodes ?? [];
  const platformId = platform?.id ?? '';

  const links = useMemo(() => {
    if (!platformId) {
      return [];
    }

    return [
      { to: `/o/${platformId}/events`, IconComponent: TimelineOutlined, label: 'Activity' },
      { to: `/o/${platformId}/templates`, IconComponent: WysiwygOutlined, label: 'Templates' },
      { to: `/o/${platformId}/workflows`, IconComponent: DynamicFormOutlined, label: 'Workflows' },
      { to: `/o/${platformId}/contacts`, IconComponent: PeopleOutlined, label: 'Contacts' },
      { to: `/o/${platformId}/settings`, IconComponent: SettingsOutlined, label: 'Settings' },
    ];
  }, [platformId]);

  if (!platform) {
    return null;
  }

  return (
    <>
      <PlatformSendingStatusAlert />
      <AppBar color="primary" position="sticky">
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Stack spacing={1} direction="row" alignItems="center">
              <WhiteWaypointLogo />

              <Divider flexItem orientation="vertical" />

              <Stack direction="row" alignItems="center">
                <ButtonLink
                  href={`/o/${platformId}`}
                  sx={{ color: 'inherit', display: { xs: 'none', md: 'flex' }, gap: 1 }}
                >
                  <Typography noWrap fontWeight="fontWeightMedium">
                    {platform.name}
                  </Typography>
                </ButtonLink>

                <Box sx={{ display: { xs: 'none', sm: 'block' }, pr: { xs: 1, md: 3 } }}>
                  <PlatformsDropdownMenu platforms={platforms} />
                </Box>
              </Stack>

              <NavigationLinksDesktop links={links} />
            </Stack>

            <NavigationLinksMobile links={links} />

            <Stack spacing={{ xs: 1, md: 2 }} direction="row" alignItems="center">
              <WpSessionDropdown />
            </Stack>
          </Toolbar>
        </Container>
      </AppBar>
      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Outlet />
      </Container>
      <SamplePlatformOnboardingManager />
    </>
  );
}
